import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import {
  CardTemplateProps,
  CustomTable,
  CustomTableRef,
  ETypeOfNotification,
  InputDate,
  Modal,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { useDispatch } from 'react-redux'
import { useCalendarHolidayService } from '../../servicesHooks/CalendarHoliday/useCalendarHolidayService'
import { CalendarHolidayItem } from '../../interface/CalendarHolidayTypes'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import CalendarHolidayAudit from './CalendarHolidayAudit'
import CalendarHolidayCrudPanel from './CalendarHolidayCrudPanel'
import './CalendarHoliday.scss'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function formatDate(dateText: string): string {
  if (!dateText) return ''
  const date = new Date(`${dateText}T00:00:00`)
  if (Number.isNaN(date.getTime())) return dateText
  return date.toLocaleDateString('es-AR')
}

function formatDateForRequest(value: Date | null): string | null {
  if (!value || Number.isNaN(value.getTime())) return null
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getCurrentYearRange() {
  const now = new Date()
  const year = now.getFullYear()
  return {
    from: new Date(year, 0, 1),
    to: new Date(year, 11, 31),
  }
}

type CalendarHolidayCardProps = CardTemplateProps & {
  onEdit: (item: CalendarHolidayItem) => void
  onDelete: (item: CalendarHolidayItem) => void
}

function CalendarHolidayCard({ entity, onEdit, onDelete }: Readonly<CalendarHolidayCardProps>) {
  const holiday = entity as CalendarHolidayItem
  const cardMenuItems: LocationActionItem[] = [
    {
      label: 'Editar',
      icon: 'pi pi-file-edit',
      action: () => onEdit(holiday),
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: () => onDelete(holiday),
    },
  ]

  return (
    <article className='calendar-holiday-mobile-card'>
      <div className='calendar-holiday-mobile-card-header'>
        <h3 className='calendar-holiday-mobile-card-title'>{formatDate(holiday.date)}</h3>
        <LocationActionMenu
          items={cardMenuItems}
          buttonClassName='p-button-text calendar-holiday-mobile-card-menu-btn'
          ariaLabel='Opciones del feriado'
          panelClassName='calendar-holiday-action-menu-panel'
          contentClassName='w-64'
        />
      </div>
      <div className='calendar-holiday-mobile-card-row'>
        <span className='calendar-holiday-mobile-card-key'>Motivo</span>
        <span className='calendar-holiday-mobile-card-value'>{holiday.reason || 'Sin motivo'}</span>
      </div>
      <div className='calendar-holiday-mobile-card-row'>
        <span className='calendar-holiday-mobile-card-key'>Descripción</span>
        <span className='calendar-holiday-mobile-card-value'>{holiday.description || '-'}</span>
      </div>
    </article>
  )
}

export default function CalendarHoliday() {
  const TABLE_ROW_HEIGHT_PX = 36
  const tableStyle: CSSProperties = {
    ['--calendar-holiday-row-height' as string]: `${TABLE_ROW_HEIGHT_PX}px`,
  }
  const dispatch = useDispatch()
  const { deleteHoliday, getCalendarHolidays } = useCalendarHolidayService()
  const tableRef = useRef<CustomTableRef>(null)
  const [selectedHoliday, setSelectedHoliday] = useState<CalendarHolidayItem | null>(null)
  const [creatingHoliday, setCreatingHoliday] = useState(false)
  const [showAudit, setShowAudit] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [holidayToDelete, setHolidayToDelete] = useState<CalendarHolidayItem | null>(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  const initialRange = useMemo(() => getCurrentYearRange(), [])
  const [dateFrom, setDateFrom] = useState<Date | null>(initialRange.from)
  const [dateTo, setDateTo] = useState<Date | null>(initialRange.to)

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  function openAdd() {
    setCreatingHoliday(true)
    setSelectedHoliday(null)
  }

  function openEdit(item: CalendarHolidayItem) {
    setCreatingHoliday(false)
    setSelectedHoliday(item)
  }

  function closeCrudPanel() {
    setCreatingHoliday(false)
    setSelectedHoliday(null)
  }

  function openDelete(item: CalendarHolidayItem) {
    setHolidayToDelete(item)
    setDeleteModalVisible(true)
  }

  function resetDateRangeToCurrentYear() {
    const nextRange = getCurrentYearRange()
    setDateFrom(nextRange.from)
    setDateTo(nextRange.to)
  }

  async function confirmDelete() {
    if (!holidayToDelete) {
      setDeleteModalVisible(false)
      return
    }

    try {
      await deleteHoliday(holidayToDelete.holiday)
      dispatch(setNotification({
        title: 'Feriado eliminado',
        desc: 'El feriado se eliminó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      tableRef.current?.refreshTable?.()
    } catch (error: unknown) {
      const response = isRecord(error) && isRecord(error.response) ? error.response : null
      const status = typeof response?.status === 'number' ? response.status : null

      if (status === 409) {
        dispatch(setNotification({
          title: 'No se puede eliminar',
          desc: 'El feriado esta siendo utilizado en otras relaciones.',
          typeOfNotification: ETypeOfNotification.WARNING,
          icon: 'pi pi-exclamation-triangle',
        }))
      } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo eliminar el feriado.',
          typeOfNotification: ETypeOfNotification.ERROR,
          icon: 'pi pi-times',
        }))
      }
    } finally {
      setDeleteModalVisible(false)
      setHolidayToDelete(null)
    }
  }

  if (creatingHoliday || selectedHoliday != null) {
    return (
      <CalendarHolidayCrudPanel
        holidayId={creatingHoliday ? -1 : (selectedHoliday?.holiday ?? -1)}
        initialHoliday={selectedHoliday}
        onClose={closeCrudPanel}
        onSaved={() => tableRef.current?.refreshTable?.()}
      />
    )
  }

  if (showAudit) {
    return <CalendarHolidayAudit onClose={() => setShowAudit(false)} />
  }

  return (
    <div className='viewContainer calendar-holiday-view'>
      <div className='calendar-holiday-header'>
        <h1 className='calendar-holiday-title'>Feriados</h1>
        <div className='calendar-holiday-header-actions'>
          {isMobile && (
            <Button
              type='button'
              icon='pi pi-clock'
              className='buttonControl calendar-holiday-mobile-audit-btn'
              onClick={() => setShowAudit(true)}
              tooltip='Historial de cambios'
              tooltipOptions={{ position: 'top', className: 'calendar-holiday-tooltip-no-arrow' }}
              aria-label='Historial de cambios'
            />
          )}
          <Button
            type='button'
            className='buttonMain'
            label='Agregar'
            icon='pi pi-plus-circle'
            onClick={openAdd}
          />
        </div>
      </div>
      <div className='calendar-holiday-divider' />

      <CustomTable
        idTable='CalendarHolidayTable'
        className='calendar-holiday-table'
        style={tableStyle}
        getItems={getCalendarHolidays}
        itemSize={TABLE_ROW_HEIGHT_PX}
        resizableColumns
        dynamicBehaviour
        CardTemplate={CalendarHolidayCard}
        cardAdditionalProps={{
          onEdit: openEdit,
          onDelete: openDelete,
        }}
        cardKeyName='holiday'
        filterRow
        hideFiltersButton
        ref={tableRef}
        params={{
          dateFrom: formatDateForRequest(dateFrom),
          dateTo: formatDateForRequest(dateTo),
        }}
      >
        <Column
          field='date'
          header='Fecha'
          filter
          style={{ width: '16rem',textAlign: 'center', marginTop: '10rem' }}
          filterElement={(
            <div className='calendar-holiday-date-filter-range'>
              <InputDate
                value={dateFrom}
                hideIcon
                placeholder='dd/mm/yyyy'
                maxDate={dateTo ?? undefined}
                onChange={(e) => {
                  setDateFrom(e?.target?.value ?? null)
                }}
              />
              <InputDate
                value={dateTo}
                hideIcon
                placeholder='dd/mm/yyyy'
                minDate={dateFrom ?? undefined}
                onChange={(e) => {
                  setDateTo(e?.target?.value ?? null)
                }}
              />
            </div>
          )}
          body={(rowData: CalendarHolidayItem) => formatDate(rowData.date)}
        />
        <Column field='reason' header='Motivo' filter filterElement={stringFilterTemplate} />
        <Column field='description' header='Descripción' filter filterElement={stringFilterTemplate} />
        <Column
          style={{ width: '3rem' }}
          header={(
            <div className='calendar-holiday-header-action-wrap'>
              <Button
                type='button'
                icon='pi pi-clock'
                className='buttonControl calendar-holiday-audit-header-btn'
                onClick={() => setShowAudit(true)}
                tooltip='Historial de cambios'
                tooltipOptions={{ position: 'top', className: 'calendar-holiday-tooltip-no-arrow' }}
                aria-label='Historial de cambios'
              />
            </div>
          )}
          filter
          filterElement={(
            <div className='calendar-holiday-filter-action-wrap'>
              <Button
                type='button'
                icon='pi pi-eraser'
                className='buttonControl'
                tooltip='Limpiar filtros'
                tooltipOptions={{ position: 'top', className: 'calendar-holiday-tooltip-no-arrow' }}
                onClick={() => {
                  tableRef.current?.internalClearFilters?.()
                  resetDateRangeToCurrentYear()
                }}
                aria-label='Limpiar filtros'
              />
            </div>
          )}
          body={(rowData: CalendarHolidayItem) => {
            const menuItems: LocationActionItem[] = [
              {
                label: 'Editar',
                icon: 'pi pi-file-edit',
                action: () => openEdit(rowData),
              },
              {
                label: 'Eliminar',
                icon: 'pi pi-trash',
                action: () => openDelete(rowData),
              },
            ]

            return (
              <div className='flex justify-content-end'>
                <LocationActionMenu
                  items={menuItems}
                  ariaLabel='Opciones del feriado'
                  buttonId={`calendarHolidayMenu_${rowData.holiday}`}
                  buttonClassName='p-button-text calendar-holiday-action-menu-btn'
                  panelClassName='calendar-holiday-action-menu-panel'
                />
              </div>
            )
          }}
        />
      </CustomTable>

      <Modal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        width='w-medium'
        header='Eliminar feriado'
        footerButtonLeftText='Cancelar'
        footerButtonRightText='Eliminar'
        onClickLeftBtn={() => setDeleteModalVisible(false)}
        onClickRightBtn={() => { void confirmDelete() }}
      >
        <p>
          ¿Queres eliminar el feriado <strong>{holidayToDelete?.reason ?? ''}</strong> del dia{' '}
          <strong>{holidayToDelete ? formatDate(holidayToDelete.date) : ''}</strong>?
        </p>
      </Modal>
    </div>
  )
}
